import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { KanbanColumn } from '@/types/customerSupport';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: KanbanColumn[];
  loading?: boolean;
  onMoveUp: (id: number) => Promise<void> | void;
  onMoveDown: (id: number) => Promise<void> | void;
  onToggleVisible: (id: number) => Promise<void> | void;
  onCreate: (data: { label: string; color: string }) => Promise<void>;
  onUpdate: (id: number, data: { label?: string; color?: string }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

const DEFAULT_NEW_COLOR = '#6366f1';

const KanbanColumnsConfigDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  columns,
  loading,
  onMoveUp,
  onMoveDown,
  onToggleVisible,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState(DEFAULT_NEW_COLOR);

  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_NEW_COLOR);
  const [creating, setCreating] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<KanbanColumn | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setShowCreate(false);
      setNewLabel('');
      setNewColor(DEFAULT_NEW_COLOR);
      setConfirmDelete(null);
    }
  }, [open]);

  const startEdit = (col: KanbanColumn) => {
    setEditingId(col.id);
    setEditLabel(col.label);
    setEditColor(col.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
    setEditColor(DEFAULT_NEW_COLOR);
  };

  const saveEdit = async (col: KanbanColumn) => {
    if (!editLabel.trim()) {
      toast({ title: 'Título obrigatório', variant: 'destructive' });
      return;
    }
    try {
      await onUpdate(col.id, { label: editLabel.trim(), color: editColor });
      toast({ title: 'Coluna atualizada' });
      cancelEdit();
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar',
        description: err?.message || 'Tente novamente',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = async () => {
    if (!newLabel.trim()) {
      toast({ title: 'Informe o nome da coluna', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      await onCreate({ label: newLabel.trim(), color: newColor });
      toast({ title: 'Coluna criada' });
      setShowCreate(false);
      setNewLabel('');
      setNewColor(DEFAULT_NEW_COLOR);
    } catch (err: any) {
      toast({
        title: 'Erro ao criar',
        description: err?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await onDelete(confirmDelete.id);
      toast({ title: 'Coluna removida' });
      setConfirmDelete(null);
    } catch (err: any) {
      toast({
        title: 'Não foi possível excluir',
        description: err?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar colunas do Kanban</DialogTitle>
            <DialogDescription>
              Reordene, renomeie, mude a cor, ative/desative ou crie novas colunas. As alterações ficam salvas no servidor e valem para todos os usuários.
            </DialogDescription>
          </DialogHeader>

          {/* Botão criar nova */}
          <div className="flex justify-end pt-2">
            {!showCreate && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nova coluna
              </Button>
            )}
          </div>

          {/* Form de criação */}
          {showCreate && (
            <div className="rounded-lg border-2 border-dashed border-primary/40 p-3 space-y-3 bg-primary/5">
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="new-col-label" className="text-xs">Nome da coluna</Label>
                  <Input
                    id="new-col-label"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    placeholder="Ex.: Em validação"
                    autoFocus
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-col-color" className="text-xs">Cor</Label>
                  <Input
                    id="new-col-color"
                    type="color"
                    value={newColor}
                    onChange={e => setNewColor(e.target.value)}
                    className="h-10 w-14 p-1 cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setNewLabel('');
                    setNewColor(DEFAULT_NEW_COLOR);
                  }}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleCreate} disabled={creating || !newLabel.trim()}>
                  {creating ? 'Criando...' : 'Criar'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2 py-2 max-h-[55vh] overflow-y-auto">
            {loading && columns.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Carregando colunas...</p>
            ) : columns.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhuma coluna cadastrada.</p>
            ) : (
              columns.map((col, index) => {
                const isFirst = index === 0;
                const isLast = index === columns.length - 1;
                const isEditing = editingId === col.id;
                return (
                  <div
                    key={col.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    {isEditing ? (
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Input
                          type="color"
                          value={editColor}
                          onChange={e => setEditColor(e.target.value)}
                          className="h-9 w-12 p-1 cursor-pointer shrink-0"
                        />
                        <Input
                          value={editLabel}
                          onChange={e => setEditLabel(e.target.value)}
                          autoFocus
                          className="flex-1"
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit(col);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: col.color }}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate flex items-center gap-2">
                            {col.label}
                            {col.is_built_in && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                padrão
                              </Badge>
                            )}
                            {!col.is_active && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                oculta
                              </Badge>
                            )}
                          </p>
                          <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0 font-mono">
                            {col.key}
                          </Badge>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-1 shrink-0">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-600"
                            onClick={() => saveEdit(col)}
                            title="Salvar"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={cancelEdit}
                            title="Cancelar"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={isFirst}
                            onClick={() => onMoveUp(col.id)}
                            title="Mover para cima"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={isLast}
                            onClick={() => onMoveDown(col.id)}
                            title="Mover para baixo"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => startEdit(col)}
                            title="Renomear / mudar cor"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Switch
                            checked={col.is_active}
                            onCheckedChange={() => onToggleVisible(col.id)}
                            aria-label={col.is_active ? 'Ocultar coluna' : 'Mostrar coluna'}
                          />
                          {!col.is_built_in && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setConfirmDelete(col)}
                              title="Excluir coluna"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir coluna "{confirmDelete?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove a coluna do kanban. Implantações que estão nela precisam ser movidas antes — caso contrário a exclusão será bloqueada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={e => {
                e.preventDefault();
                handleConfirmDelete();
              }}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default KanbanColumnsConfigDialog;
